import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Panel, Btn, PageHead, StatusDot } from "@/components/kit";
import { Search, Radar, Mail, ArrowRight, SlidersHorizontal, Star, X, Archive, Trash2, BarChart3, Globe, Sparkles, TrendingUp } from "lucide-react";

const hostOf = (u) => {
    try { return new URL(u).hostname.replace("www.", ""); } catch { return u; }
};

const oppColor = (o) => ({ high: "#39FF14", medium: "#FFD700", low: "#FF003C" }[o] || "#4A4A4A");

const rpmValue = (s) => {
    const nums = String(s || "").match(/\d+(\.\d+)?/g);
    if (!nums || !nums.length) return 0;
    return nums.reduce((a, b) => a + parseFloat(b), 0) / nums.length;
};

const FSelect = ({ label, value, onChange, options, testId }) => (
    <div>
        <div className="label mb-1">{label}</div>
        <select
            data-testid={testId}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full border border-input bg-background px-2 py-1.5 text-xs outline-none focus:border-primary"
        >
            {options.map(([v, t]) => (
                <option key={v} value={v}>{t}</option>
            ))}
        </select>
    </div>
);

export default function Niches() {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [topic, setTopic] = useState("");
    const [audience, setAudience] = useState("general");
    const [sortBy, setSortBy] = useState("score");
    const [fOpp, setFOpp] = useState("all");
    const [fComp, setFComp] = useState("all");
    const [fFormat, setFFormat] = useState("all");
    const [fMode, setFMode] = useState("all");
    const [fView, setFView] = useState("active");
    const winnersOn = fOpp === "high" && fComp === "low" && sortBy === "score";

    const { data: niches = [] } = useQuery({
        queryKey: ["niches"],
        queryFn: async () => (await api.get("/niches")).data,
    });

    const { data: trendsData } = useQuery({
        queryKey: ["niche-trends"],
        queryFn: async () => (await api.get("/niches/trends")).data,
    });
    const trends = trendsData?.trends || [];

    const filtered = useMemo(() => {
        let list = [...niches];
        if (fView === "active") list = list.filter((n) => !n.archived);
        else if (fView === "favorites") list = list.filter((n) => n.favorite);
        else if (fView === "archived") list = list.filter((n) => n.archived);
        if (fOpp !== "all") list = list.filter((n) => n.opportunity === fOpp);
        if (fComp !== "all") list = list.filter((n) => n.competition === fComp);
        if (fFormat !== "all") list = list.filter((n) => String(n.best_format || "").includes(fFormat));
        if (fMode !== "all") list = list.filter((n) => (n.mode || "manual") === fMode);
        if (sortBy === "score") list.sort((a, b) => (b.score || 0) - (a.score || 0));
        else if (sortBy === "rpm") list.sort((a, b) => rpmValue(b.rpm_estimate) - rpmValue(a.rpm_estimate));
        else list.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
        return list;
    }, [niches, sortBy, fOpp, fComp, fFormat, fMode, fView]);

    const resetFilters = () => {
        setSortBy("score"); setFOpp("all"); setFComp("all"); setFFormat("all"); setFMode("all");
    };

    const patchNiche = useMutation({
        mutationFn: async ({ id, body }) => (await api.patch(`/niches/${id}`, body)).data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ["niches"] }),
        onError: () => toast.error("Fallo actualizando el nicho"),
    });

    const delNiche = useMutation({
        mutationFn: async (id) => (await api.delete(`/niches/${id}`)).data,
        onSuccess: () => {
            toast("Nicho eliminado", { icon: "🗑" });
            qc.invalidateQueries({ queryKey: ["niches"] });
        },
        onError: () => toast.error("Fallo eliminando el nicho"),
    });

    const purge = useMutation({
        mutationFn: async () => (await api.delete("/niches/purge")).data,
        onSuccess: (d) => {
            toast.success(`Limpieza: ${d.deleted} nichos no favoritos eliminados`, { duration: 6000 });
            qc.invalidateQueries({ queryKey: ["niches"] });
        },
        onError: () => toast.error("Fallo en la limpieza"),
    });
    const purgeCount = niches.filter((n) => !n.favorite && !n.archived).length;

    const auto = useMutation({
        mutationFn: async () => (await api.post("/niches/autoresearch")).data,
        onSuccess: (d) => {
            toast.success(`darkzOGx detectó ${d.niches?.length || 0} nichos rentables por su cuenta`);
            qc.invalidateQueries({ queryKey: ["niches"] });
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Fallo en la investigación autónoma"),
    });

    const research = useMutation({
        mutationFn: async () =>
            (await api.post("/niches/research", { topic, audience, language: "es" })).data,
        onSuccess: () => {
            toast.success("Investigación completada por darkzOGx → Minimax");
            qc.invalidateQueries({ queryKey: ["niches"] });
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Fallo en la investigación"),
    });

    return (
        <div data-testid="niches-page">
            <PageHead
                title="RECOMENDADOR DE NICHOS"
                sub="darkzOGx propone nichos y los VALIDA con datos reales: mide demanda y competencia en YouTube (Data API) y consulta el RPM y las tendencias 2026 en fuentes expertas de la web. Cada nicho llega rankeado con su evidencia."
            />

            <Panel
                title="Investigación autónoma · darkzOGx decide"
                testId="niche-auto-panel"
                className="mb-6 border-primary/60"
            >
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <p className="max-w-xl text-xs text-muted-foreground">
                        No necesitas darle un tema: el agente propone candidatos, mide su demanda y
                        competencia REAL en YouTube y consulta el RPM en fuentes expertas de la web.
                        Prioriza nichos adyacentes a tu canal y los más rentables en general.
                    </p>
                    <Btn
                        testId="niche-auto-btn"
                        onClick={() => auto.mutate()}
                        disabled={auto.isPending}
                        className="shrink-0"
                    >
                        <span className="flex items-center gap-2">
                            <Radar size={14} />
                            {auto.isPending ? "Investigando…" : "Recomiéndame nichos"}
                        </span>
                    </Btn>
                </div>
                {auto.isPending && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-primary">
                        <StatusDot color="#00FFFF" pulse /> darkzOGx midiendo demanda/competencia real en YouTube + RPM en la web (~30-40s)…
                    </div>
                )}
            </Panel>

            {trends.length > 0 && (
                <Panel
                    title="Tendencias · índice de outliers (histórico real)"
                    right={<TrendingUp size={15} className="text-primary" />}
                    testId="niche-trends-panel"
                    className="mb-6"
                >
                    <p className="mb-3 max-w-2xl text-xs text-muted-foreground">
                        Cada vez que investigo un nicho guardo sus señales REALES de YouTube. Aquí veo
                        cómo evoluciona la demanda en el tiempo y detecto <span className="text-status-green">nichos emergentes</span>{" "}
                        (demanda subiendo + competencia baja) — {trendsData?.total_probes || 0} mediciones acumuladas.
                    </p>
                    <div className="grid gap-2 md:grid-cols-2">
                        {trends.map((t, i) => (
                            <div
                                key={i}
                                className="flex items-center justify-between border border-border/60 px-3 py-2 text-xs"
                                data-testid={`trend-${i}`}
                            >
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        {t.emerging && (
                                            <span className="border border-status-green px-1 py-0.5 font-mono text-[8px] uppercase text-status-green">
                                                emergente
                                            </span>
                                        )}
                                        <span className="truncate">{t.niche_name}</span>
                                    </div>
                                    <div className="mt-0.5 font-mono text-[10px] uppercase text-muted-foreground">
                                        {t.avg_views.toLocaleString("es")} vistas · comp {t.competition} · {t.samples} medic.
                                    </div>
                                </div>
                                <div
                                    className="shrink-0 font-mono text-sm"
                                    style={{ color: t.demand_delta_pct >= 0 ? "#39FF14" : "#FF003C" }}
                                    title="Cambio de demanda desde la primera medición"
                                >
                                    {t.demand_delta_pct >= 0 ? "+" : ""}{t.demand_delta_pct}%
                                </div>
                            </div>
                        ))}
                    </div>
                </Panel>
            )}

            <Panel title="Investigación dirigida (opcional)" testId="niche-form" className="mb-6">
                <div className="flex flex-col gap-3 md:flex-row">
                    <input
                        data-testid="niche-topic-input"
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        placeholder="Tema o mercado (ej: finanzas personales para jóvenes)"
                        className="flex-1 border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                    />
                    <input
                        data-testid="niche-audience-input"
                        value={audience}
                        onChange={(e) => setAudience(e.target.value)}
                        placeholder="Audiencia"
                        className="border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary md:w-48"
                    />
                    <Btn
                        testId="niche-research-btn"
                        variant="ghost"
                        onClick={() => topic.trim() && research.mutate()}
                        disabled={research.isPending || !topic.trim()}
                    >
                        <span className="flex items-center gap-2">
                            <Search size={14} />
                            {research.isPending ? "Analizando…" : "Investigar"}
                        </span>
                    </Btn>
                </div>
                {research.isPending && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-primary">
                        <StatusDot color="#00FFFF" pulse /> Minimax procesando tendencias…
                    </div>
                )}
            </Panel>

            {niches.length > 0 && (
                <Panel
                    title="Filtros · encuentra los mejores"
                    testId="niche-filters"
                    className="mb-6"
                    right={<SlidersHorizontal size={14} className="text-primary" />}
                >
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
                        <FSelect
                            label="Vista"
                            value={fView}
                            onChange={setFView}
                            testId="niche-view-select"
                            options={[["active", "Activos"], ["favorites", "★ Favoritos"], ["archived", "Archivados"], ["all", "Todos"]]}
                        />
                        <FSelect
                            label="Ordenar por"
                            value={sortBy}
                            onChange={setSortBy}
                            testId="niche-sort-select"
                            options={[["score", "Score (mejores)"], ["rpm", "RPM (más productivos)"], ["recent", "Más recientes"]]}
                        />
                        <FSelect
                            label="Oportunidad"
                            value={fOpp}
                            onChange={setFOpp}
                            testId="niche-opp-select"
                            options={[["all", "Todas"], ["high", "Alta (viral)"], ["medium", "Media"], ["low", "Baja"]]}
                        />
                        <FSelect
                            label="Competencia"
                            value={fComp}
                            onChange={setFComp}
                            testId="niche-comp-select"
                            options={[["all", "Todas"], ["low", "Baja (fácil entrar)"], ["medium", "Media"], ["high", "Alta"]]}
                        />
                        <FSelect
                            label="Formato"
                            value={fFormat}
                            onChange={setFFormat}
                            testId="niche-format-select"
                            options={[["all", "Todos"], ["short", "Shorts"], ["long", "Long-form"], ["mixed", "Mixto"]]}
                        />
                        <FSelect
                            label="Origen"
                            value={fMode}
                            onChange={setFMode}
                            testId="niche-mode-select"
                            options={[["all", "Todos"], ["auto", "Autónomo (AUTO)"], ["manual", "Dirigido"]]}
                        />
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                        <button
                            data-testid="niche-winners-btn"
                            onClick={() => {
                                if (winnersOn) resetFilters();
                                else { setFOpp("high"); setFComp("low"); setSortBy("score"); }
                            }}
                            className={`flex items-center gap-2 border px-3 py-1.5 text-[11px] uppercase tracking-wider transition-colors ${
                                winnersOn
                                    ? "border-primary bg-primary text-primary-foreground"
                                    : "border-border text-muted-foreground hover:border-primary hover:text-primary"
                            }`}
                        >
                            <Star size={12} /> Solo ganadores (viral + baja competencia)
                        </button>
                        <button
                            data-testid="niche-clear-filters"
                            onClick={resetFilters}
                            className="flex items-center gap-2 border border-border px-3 py-1.5 text-[11px] uppercase tracking-wider text-muted-foreground hover:border-destructive hover:text-destructive"
                        >
                            <X size={12} /> Limpiar
                        </button>
                        <span className="text-[11px] text-muted-foreground" data-testid="niche-filter-count">
                            {filtered.length} de {niches.length} nichos
                        </span>
                        <button
                            data-testid="niche-purge-btn"
                            onClick={() => {
                                if (window.confirm(`Se borrarán ${purgeCount} nichos que NO son favoritos ni archivados. ¿Continuar?`)) {
                                    purge.mutate();
                                }
                            }}
                            disabled={purge.isPending || purgeCount === 0}
                            className="ml-auto flex items-center gap-2 border px-3 py-1.5 text-[11px] uppercase tracking-wider transition-colors disabled:opacity-40"
                            style={{ borderColor: "#FF003C", color: "#FF003C" }}
                        >
                            <Trash2 size={12} />
                            {purge.isPending ? "Borrando…" : `Borrar no favoritos (${purgeCount})`}
                        </button>
                    </div>
                </Panel>
            )}

            <div className="grid grid-cols-1 gap-px bg-border md:grid-cols-2 xl:grid-cols-3">
                {filtered.map((n) => (
                    <div key={n.id} className="bg-card p-5" data-testid={`niche-card-${n.id}`}>
                        <div className="mb-3 flex items-start justify-between">
                            <h3 className="font-head text-lg leading-tight">
                                {n.name}
                                {n.mode === "auto" && (
                                    <span
                                        className="ml-2 inline-block border px-1.5 py-0.5 align-middle font-mono text-[9px] uppercase tracking-wider"
                                        style={{ color: "#00FFFF", borderColor: "#00FFFF" }}
                                    >
                                        Auto
                                    </span>
                                )}
                                {n.adjacent && (
                                    <span
                                        className="ml-2 inline-flex items-center gap-1 border px-1.5 py-0.5 align-middle font-mono text-[9px] uppercase tracking-wider"
                                        style={{ color: "#39FF14", borderColor: "#39FF14" }}
                                        title="Aprovecha la audiencia de tu canal actual"
                                    >
                                        <Sparkles size={9} /> Adyacente
                                    </span>
                                )}
                            </h3>
                            <div className="flex shrink-0 items-center gap-1.5">
                                <button
                                    data-testid={`niche-fav-${n.id}`}
                                    title={n.favorite ? "Quitar de favoritos" : "Marcar favorito"}
                                    onClick={() => patchNiche.mutate({ id: n.id, body: { favorite: !n.favorite } })}
                                    className="p-0.5 transition-colors"
                                    style={{ color: n.favorite ? "#FFD700" : "#4A4A4A" }}
                                >
                                    <Star size={15} fill={n.favorite ? "#FFD700" : "none"} />
                                </button>
                                <button
                                    data-testid={`niche-archive-${n.id}`}
                                    title={n.archived ? "Desarchivar" : "Archivar"}
                                    onClick={() => patchNiche.mutate({ id: n.id, body: { archived: !n.archived } })}
                                    className="p-0.5 transition-colors hover:text-primary"
                                    style={{ color: n.archived ? "#00FFFF" : "#4A4A4A" }}
                                >
                                    <Archive size={14} />
                                </button>
                                <button
                                    data-testid={`niche-delete-${n.id}`}
                                    title="Eliminar nicho"
                                    onClick={() => {
                                        if (window.confirm(`¿Eliminar el nicho "${n.name}"?`)) delNiche.mutate(n.id);
                                    }}
                                    className="p-0.5 text-[#4A4A4A] transition-colors hover:text-destructive"
                                >
                                    <Trash2 size={14} />
                                </button>
                                <span className="font-head text-2xl text-primary">{n.score}</span>
                            </div>
                        </div>
                        <p className="mb-4 text-xs text-muted-foreground">{n.summary}</p>
                        <div className="mb-4 grid grid-cols-2 gap-2 text-[11px]">
                            <div className="flex items-center gap-2">
                                <StatusDot color={oppColor(n.opportunity)} /> Oport: {n.opportunity}
                            </div>
                            <div className="flex items-center gap-2">
                                <StatusDot color={oppColor(n.competition === "low" ? "high" : n.competition === "high" ? "low" : "medium")} />{" "}
                                Comp: {n.competition}
                            </div>
                            <div>RPM: <span className="text-foreground">{n.rpm_estimate}</span></div>
                            <div>Formato: <span className="text-primary uppercase">{n.best_format}</span></div>
                        </div>
                        {n.evidence && (
                            <div
                                className="mb-4 border-l-2 bg-muted/30 p-2 text-[11px] leading-relaxed"
                                style={{ borderColor: n.data_source === "youtube_real" ? "#39FF14" : "#FFD700" }}
                                data-testid={`niche-evidence-${n.id}`}
                            >
                                <div
                                    className="mb-1 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider"
                                    style={{ color: n.data_source === "youtube_real" ? "#39FF14" : "#FFD700" }}
                                >
                                    {n.data_source === "youtube_real"
                                        ? <><BarChart3 size={11} /> Datos reales de YouTube</>
                                        : <><Radar size={11} /> Estimación del modelo</>}
                                </div>
                                <p className="text-muted-foreground">{n.evidence}</p>
                            </div>
                        )}
                        <div className="flex flex-wrap gap-1">
                            {(n.keywords || []).slice(0, 5).map((k, i) => (
                                <span key={i} className="border border-border px-2 py-0.5 text-[10px] text-muted-foreground">
                                    {k}
                                </span>
                            ))}
                        </div>
                        {(n.suggested_email || n.suggested_handle) && (
                            <div className="mt-3 border border-border p-2 text-[11px]" data-testid={`niche-email-${n.id}`}>
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <Mail size={11} className="shrink-0 text-primary" />
                                    <span className="break-all font-mono text-primary">
                                        {n.suggested_email || "—"}
                                    </span>
                                </div>
                                {n.suggested_handle && (
                                    <div className="mt-1 text-muted-foreground">
                                        Handle: <span className="text-foreground">{n.suggested_handle}</span>
                                    </div>
                                )}
                            </div>
                        )}
                        {(n.sources || []).length > 0 && (
                            <div className="mt-3 text-[10px]" data-testid={`niche-sources-${n.id}`}>
                                <div className="mb-1 flex items-center gap-1 text-muted-foreground">
                                    <Globe size={10} className="text-primary" /> Fuentes web analizadas
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {n.sources.slice(0, 4).map((s, i) => (
                                        <a
                                            key={i}
                                            href={s.uri}
                                            target="_blank"
                                            rel="noreferrer"
                                            title={s.title || s.uri}
                                            className="max-w-[150px] truncate border border-border px-1.5 py-0.5 text-primary transition-colors hover:border-primary"
                                        >
                                            {hostOf(s.uri)}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                        <Btn
                            testId={`niche-to-kit-${n.id}`}
                            variant="green"
                            className="mt-3 w-full"
                            onClick={() =>
                                navigate(`/kit?niche_id=${n.id}&niche=${encodeURIComponent(n.name)}`)
                            }
                        >
                            <span className="flex items-center justify-center gap-2">
                                Crear kit con este nicho <ArrowRight size={13} />
                            </span>
                        </Btn>
                    </div>
                ))}
            </div>
            {!niches.length && (
                <p className="mt-6 text-sm text-muted-foreground">
                    Aún no hay nichos. Pulsa &quot;Recomiéndame nichos&quot; y darkzOGx investigará por ti.
                </p>
            )}
            {niches.length > 0 && !filtered.length && (
                <p className="mt-6 text-sm text-muted-foreground" data-testid="niche-no-results">
                    Ningún nicho cumple estos filtros. Pulsa &quot;Limpiar&quot; o lanza una nueva investigación autónoma.
                </p>
            )}
        </div>
    );
}
